/**
 * @file This file is a helper class for its sibling class Table.ts. It provides the logic needed to support the multi-view multi-table scenario (in LR and ALP).
 */

import type { Definition } from 'typescript-json-schema';
import i18next from 'i18next';
import type { AnnotationTerm } from '@sap-ux/vocabularies-types';
import type { LineItem } from '@sap-ux/vocabularies-types/vocabularies/UI';
import type * as Edm from '@sap-ux/vocabularies-types/Edm';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { FacetBase, LogSeverity, SchemaTag, UIVOCABULARYDOT } from '@sap/ux-specification-types';
import type {
    AccessorPath,
    GenerateAppSchemaParameters,
    ProcessingRule,
    SchemaHandlingParams,
    SyncRule
} from '@sap/ux-specification-types';
import * as v2 from '@sap/ux-specification-types/src/v2/genericSchemaHandling/controls/Table';
import { log } from '../../../../extensionLogger';
import { syncRule } from '../../../common/decoration';
import { getManifestPathToPageSettings } from '../../utils';
import {
    getLineItemInfo,
    retrieveNameWithoutVocabularyFromVisualization,
    type LineItemInfo
} from '../../generate/utils';
import {
    DISALLOWED_WITHOUT_EXPLANATION,
    getSyncRuleForManifestProperty,
    hasPropertyTypeError
} from '../../generate/manifestPropertyUtils';
import type {
    DisallowedSpecification,
    ElementAdapterExtended,
    ManifestRule
} from '../../generate/manifestPropertyUtils';
import { replaceSpecialCharsInId } from '../../generate/stableIdUtils';
import { checkTableType, getDefaultTableType, Table } from './Table';
import type { TableParams } from './Table';
import { addEnumForValidAnnotations, addEnumToSchema, DEFINITION_LINK_PREFIX } from '../../../common';

/**
 * This type is used to collect information about the usage of different table types in multi-view multi-table scenarios.
 *
 * @property responsive - number of views using responsive table
 * @property nonResponsive - number of views using non-responsive table
 */
export type TableTypeUsage = {
    responsive: number;
    nonResponsive: number;
};

/**
 * An instance of this type represents one view in a multi-view multi-table page.
 * The instances are created in function getVariantInfo.
 *
 * @property accessor - the property name in manifest which is used to address the variant
 * @property iconTabFilterKey - escaped version of the key in variantDefinition
 * @property variantDefinition - the definition of the view from the manifest
 * @property isChart - is this view a chart?
 * @property tableType - the table type which will be used for this view (if it is not a chart). Todo: Can we use type TableTypeV2Enum here?
 * @property smartControlId - the control id of the smart control representing this view
 * @property description - human readable description of this view
 * @property lineItemInfo - the line item this table is built on
 * @property entitySetDefinition - definition of the entity set shown by this view
 * @property entityTypeDefinition - definition of the entity type for the entity set
 */
export type VariantInfo = {
    accessor: string;
    iconTabFilterKey: string;
    variantDefinition: object;
    isChart: boolean;
    tableType?: v2.TableTypeV2Enum;
    smartControlId: string;
    description: string;
    lineItemInfo?: LineItemInfo;
    entitySetDefinition?: Edm.EntitySet;
    entityTypeDefinition?: Edm.EntityType;
};

/**
 * An instance of this type collects information about all views in a multi-view multi-table page.
 * Thus, only one instance of this type will be created per multi-view multi-table page (in function getMultiTableAllVariantsInfo).
 *
 * @property tableParams - the TableParams valid for the multi-table scenario as a whole
 * @property variantsToInfo - for each view the corresponding information.
 * Note that the key in this map corresponds to the property name in manifest which is used to address the view (accessor in VariantInfo).
 */
type MultiTableAllVariantsInfo = {
    tableParams: TableParams;
    variantsToInfo: { [key: string]: VariantInfo };
};

/**
 * Instances of this type are used when dealing with multi-view multi-table pages.
 * In that scenario they are used for two purposes:
 * a) One instance represents the general settings defined for all views (created in function processingRuleAdapter for property table of class ListReportNew and enhanced later).
 * b) For each view there is one instance representing it. This is also true if the view actually displays a chart (which will be considered as a 'special table' in this context).
 * These instances are created in the processingRuleAdapter of class MultiViewsVariants.
 *
 * @property variantInfo - undefined in scenario a). In scenario b) it contains information about the view represented.
 * @property possibleEntitySets - has identical values for all instances used in a multi-view multi-table page. Contains the list of all entity sets that can be used for a view.
 * @property usedTableTypes - has identical values for all instances used in a multi-view multi-table page. Contains the information which table type is currently used how often.
 */
export type MultiTableInfo = {
    variantInfo?: VariantInfo;
    possibleEntitySets?: string[];
    usedTableTypes: TableTypeUsage;
};

// MultiViewsVariants
@syncRule({
    // Task of this function is to process the list of views (provided in MultiTableAllVariantsInfo.variantsToInfo) and transform them into properties
    // of processingRule.element. Moreover, the further processing of these child properties is prepared by creating a suitable ProcessingRulesAdapter.
    processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
        const multiTableAllVariantsInfo = schemaHandlingParams.specificParams as MultiTableAllVariantsInfo;
        const properties: {
            [key: string]: Definition;
        } = {};
        let i = 1;
        for (const key in multiTableAllVariantsInfo.variantsToInfo) {
            const variantInfo = multiTableAllVariantsInfo.variantsToInfo[key];
            const propertyDefinition = {
                $ref: DEFINITION_LINK_PREFIX + 'Table'
            } as Definition;
            propertyDefinition[SchemaTag.propertyIndex] = i;
            propertyDefinition[SchemaTag.descriptionSrcURL] =
                'https://ui5.sap.com/sdk/#/topic/97dfeea4a8c346368cea6bf7a3e85478';
            propertyDefinition[SchemaTag.icon] = variantInfo.isChart ? 'Chart' : 'Table';
            propertyDefinition[SchemaTag.deletable] = true;
            propertyDefinition.description = variantInfo.description;
            properties[key] = propertyDefinition;
            i++;
        }
        processingRule.element.additionalProperties = false;
        processingRule.element.properties = properties;
        const processingRulesAdapter = function (property: string, processingRule: ProcessingRule): void {
            const tableParams = { ...multiTableAllVariantsInfo.tableParams } as TableParams;
            const variantInfo = multiTableAllVariantsInfo.variantsToInfo[property];
            tableParams.multiTableInfo = {
                usedTableTypes: tableParams.multiTableInfo.usedTableTypes,
                variantInfo
            };
            processingRule.referenceAdaptation = {
                suffix: `View-${variantInfo.iconTabFilterKey}`, // ensure that a specific copy of type Table will be created in the specific schema
                syncRuleProvider: Table,
                specificParams: tableParams
            };
        };
        processingRule.referenceAdaptation = {
            processingRulesAdapter
        };
    }
})
export class MultiViewsVariants implements v2.MultiViewsVariants {
    [key: string]: v2.Table; // Note that the key in this map corresponds to the property name in manifest which is used to address the view (accessor in VariantInfo).
}

/**
 * The syncRule for all manifest based direct child properties of quickVariantSelectionX.
 */
const syncRuleForQuickVariantSelectionXProperty: SyncRule = {
    manifest: {},
    processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
        processingRule.manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, [
            'quickVariantSelectionX'
        ]);
    }
};

/**
 * Gets the entity type definition for a view.
 *
 * @param variantDefinition - definition of the view from the manifest
 * @param entityType - the entity type of the entity set which is defined for this LR (used for all views which do not explicitly use a different entity set)
 * @param generateParameters - list of API input parameters
 * @returns the definition of the relevant entity type (or undefined when no entity type could be determined)
 */
function getEntityDefinition(
    variantDefinition: object,
    entityType: Edm.EntityType,
    generateParameters: GenerateAppSchemaParameters
): { entitySetDefinition: Edm.EntitySet; entityTypeDefinition: Edm.EntityType | undefined } | undefined {
    if (hasPropertyTypeError(variantDefinition, 'entitySet', generateParameters.logger, 'string')) {
        // problem is already logged by helper method
        return;
    }
    const entitySetName = variantDefinition['entitySet'] as string | undefined; // if undefined the main entity set of the LR is used
    const entitySetDefinition = entitySetName
        ? generateParameters.serviceAVT.entitySets.find(
              (entitySetDefinition) => entitySetDefinition.name === entitySetName
          )
        : generateParameters.entitySet;
    if (entitySetName && !entitySetDefinition) {
        // illegal entity set specified in the manifest
        log(generateParameters.logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOENTITYTYPE4SET', { property: entitySetName })
        });
        return;
    }
    const entityTypeDefinition = entitySetDefinition ? entitySetDefinition.entityType : entityType;
    return { entitySetDefinition, entityTypeDefinition };
}

/**
 * This function retrieves the annotation based information for a view that has been defined in the manifest (under quickVariantSelectionX.variants).
 * Note that the logic needs to be consistent with the corresponding logic in Fiori Elements.
 * In Fiori Elements this logic is spread around and partially redundant.
 * A starting point may be function getLineItemFromVariant in sap.suite.ui.generic.template.js.preparationHelper.
 *
 * @param variant - the view definition from the manifest
 * @param getUiAnnotation - function to retrieve ui annotations based on their path
 * @returns -  variantDescription: human readable description of this view
 * - isChart: is this view a chart?
 * - lineItemName: name of the line item annotation used for this view (undefined if the view is a chart)
 */
function retrieveInfoForVariant(
    variant: object,
    getUiAnnotation: (path: string) => object
): { variantDescription: string; isChart: boolean; lineItemName: string | undefined } {
    const annotationPath = variant['annotationPath'];
    let variantDescription: string;
    let isChart: boolean = false;
    let lineItemName: string;

    if (annotationPath) {
        const uiAnnotation = getUiAnnotation(annotationPath);
        if (uiAnnotation) {
            variantDescription = uiAnnotation['Text'];
            let presentationVariant = uiAnnotation[FacetBase.PresentationVariant];
            if (presentationVariant?.Path) {
                presentationVariant = getUiAnnotation(presentationVariant.Path);
            }
            if (presentationVariant) {
                const visualizations = presentationVariant.Visualizations;
                // In multi-table scenarios Fiori Elements only considers the first visualization entry
                const relevantVisualization = visualizations && visualizations[0];
                if (relevantVisualization) {
                    const chartName = retrieveNameWithoutVocabularyFromVisualization(
                        FacetBase.Chart,
                        relevantVisualization
                    );
                    if (chartName) {
                        isChart = true;
                    } else {
                        lineItemName = retrieveNameWithoutVocabularyFromVisualization(
                            FacetBase.LineItem,
                            relevantVisualization
                        );
                    }
                }
            }
        }
    }
    if (!isChart) {
        lineItemName = lineItemName || FacetBase.LineItem;
    }
    variantDescription = variantDescription || variant['key']; // take the Text from the annotation and use the specified key as a fallback

    return { variantDescription, isChart, lineItemName };
}

/**
 * Provide the VariantInfo instance for a view defined in the manifest.
 *
 * @param variantSelector - the property name in manifest which is used to address the view
 * @param variantDefinition - the definition of the view from the manifest
 * @param usedTableTypes - reference to the object collecting the usage information of different table types
 * @param generateParameters - parameters used for generating the app schema
 * @param tableParams - parameters for the table view node hosting the collection of views
 * @param defaultTableSettings - the page global tableSettings object from the manifest, resp. an empty object if not defined
 * @returns the instance of VariantInfo representing the view
 */
function getVariantInfo(
    variantSelector: string,
    variantDefinition: object,
    usedTableTypes: TableTypeUsage,
    generateParameters: GenerateAppSchemaParameters,
    tableParams: TableParams,
    defaultTableSettings: object
): VariantInfo {
    // Note that the id of SmartTable and SmartChart in the multi-table case are built in the same way.
    // For SmartTable this is defined in sap.suite.ui.generic.template.ListReport.view.fragments.SmartTable.fragment.xml together with
    // sap.suite.ui.generic.template.lib.StableIdDefinition, type: ListReport, subType: SmartTable.
    // Note that sap.suite.ui.generic.template.js.StableIdHelper.getStableId will not call escapeIdParameter on the parameter (sQuickVariantKey),
    // since the definition provides a value function.
    // For charts the same pattern is used. In this case it is defined in sap.suite.ui.generic.template.ListReport.view.fragments.SmartChart.fragment.xml
    // using function getSuffixFromIconTabFilterKey in class sap.suite.ui.generic.template.js.AnnotationHelper which also does not perform any escaping.
    const smartControlId = `${tableParams.tableId}-${variantDefinition['key']}`;
    const metaInfo = getEntityDefinition(variantDefinition, tableParams.entityType, generateParameters);
    const entityTypeDefinition = metaInfo?.entityTypeDefinition;
    if (!entityTypeDefinition?.annotations?.UI) {
        return {
            accessor: variantSelector,
            iconTabFilterKey: replaceSpecialCharsInId(variantDefinition['key'], generateParameters),
            variantDefinition,
            isChart: false,
            smartControlId,
            description: variantDefinition['key']
        };
    }
    // convenience function for accessing the ui annotations (removing unwanted prefixes)
    const getUiAnnotation = function (path: string): object {
        if (!path) {
            return;
        }
        const accessor = path.replace(UIVOCABULARYDOT, '');
        return entityTypeDefinition.annotations.UI[accessor] as object;
    };
    const { variantDescription, isChart, lineItemName } = retrieveInfoForVariant(variantDefinition, getUiAnnotation);
    let lineItemInfo: LineItemInfo | undefined;
    let tableType: v2.TableTypeV2Enum;
    if (!isChart) {
        if (hasPropertyTypeError(variantDefinition, 'tableSettings', generateParameters.logger, 'object')) {
            return {
                accessor: variantSelector,
                iconTabFilterKey: replaceSpecialCharsInId(variantDefinition['key'], generateParameters),
                variantDefinition: variantDefinition,
                isChart: false,
                smartControlId,
                description: variantDescription
            };
        }
        const variantTableSettings = (variantDefinition['tableSettings'] as object) || undefined;
        if (variantTableSettings) {
            if (
                hasPropertyTypeError(variantTableSettings, 'type', generateParameters.logger, 'string') ||
                !checkTableType(variantTableSettings['type'], generateParameters.logger)
            ) {
                return {
                    accessor: variantSelector,
                    iconTabFilterKey: replaceSpecialCharsInId(variantDefinition['key'], generateParameters),
                    variantDefinition: variantDefinition,
                    isChart: false,
                    smartControlId,
                    description: variantDescription
                };
            }
            tableType = variantTableSettings['type'] as v2.TableTypeV2Enum;
        }
        tableType = tableType || defaultTableSettings['type'] || getDefaultTableType(entityTypeDefinition);
        if (tableType === v2.TableTypeV2Enum.ResponsiveTable) {
            usedTableTypes.responsive = usedTableTypes.responsive + 1;
        } else {
            usedTableTypes.nonResponsive = usedTableTypes.nonResponsive + 1;
        }

        const lineItemDefinition = getUiAnnotation(lineItemName) as AnnotationTerm<LineItem>;
        if (lineItemDefinition) {
            lineItemInfo = getLineItemInfo(
                lineItemName,
                lineItemDefinition,
                entityTypeDefinition,
                tableType,
                generateParameters,
                false
            );
        }
    }
    const entitySetDefinition = metaInfo.entitySetDefinition; // if metaInfo would have been undefined we would have returned already
    return {
        accessor: variantSelector,
        iconTabFilterKey: replaceSpecialCharsInId(variantDefinition['key'], generateParameters),
        variantDefinition,
        isChart,
        smartControlId,
        description: variantDescription,
        lineItemInfo,
        entityTypeDefinition,
        entitySetDefinition,
        tableType
    };
}

/**
 * This function analyses all views defined in the manifest and collects information about them.
 *
 * @param variants - the views defined in the manifest
 * @param generateParameters - parameters used for generating the app schema
 * @param tableParams - parameters for the table view node hosting the collection of views
 * @param defaultTableSettings - the page global tableSettings object from the manifest, resp. an empty object if not defined
 * @returns the instance of VariantInfo representing the views and the usage information of different table types.
 * Note that variantsToInfo will be undefined in case there is a severe inconsistency in the manifest definition of the views.
 * In this case an error will also have been logged.
 */
function getVariantsToInfo(
    variants: object,
    generateParameters: GenerateAppSchemaParameters,
    tableParams: TableParams,
    defaultTableSettings: object
): { variantsToInfo: { [key: string]: VariantInfo } | undefined; usedTableTypes: TableTypeUsage } {
    const variantsToInfo: { [key: string]: VariantInfo } = {};
    const usedTableTypes = {
        responsive: 0,
        nonResponsive: 0
    };
    const hasErrors = Object.keys(variants).some(function (variantSelector) {
        if (hasPropertyTypeError(variants, variantSelector, generateParameters.logger, 'object', true)) {
            return true;
        }
        const variantDefinition = variants[variantSelector] as object; // the variant definition from the manifest
        if (!variantDefinition['key']) {
            log(generateParameters.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOKEY4VIEW', { property: variantSelector })
            });
            return true;
        }
        const variantInfo = getVariantInfo(
            variantSelector,
            variantDefinition,
            usedTableTypes,
            generateParameters,
            tableParams,
            defaultTableSettings
        );
        variantsToInfo[variantSelector] = variantInfo;
    });
    return { variantsToInfo: hasErrors ? undefined : variantsToInfo, usedTableTypes };
}

/**
 * Retrieves the information about all views defined in the manifest for a multi-view multi-table scenario and returns them as instance of MultiTableAllVariantsInfo.
 *
 * @param tableParams - parameters for the table view node hosting the collection of views
 * @param generateParameters - parameters used for generating the app schema
 * @returns the instance of MultiTableAllVariantsInfo representing the views. Undefined in case there is a severe inconsistency in the manifest definition of the views.
 * In this case an error will also have been logged.
 */
function getMultiTableAllVariantsInfo(
    tableParams: TableParams,
    generateParameters: GenerateAppSchemaParameters
): MultiTableAllVariantsInfo | undefined {
    const quickVariantSelectionX = tableParams.settings.quickVariantSelectionX;
    if (hasPropertyTypeError(quickVariantSelectionX, 'variants', generateParameters.logger, 'object', true)) {
        return undefined;
    }
    const defaultTableSettings = (tableParams.settings?.tableSettings || {}) as object;
    const { variantsToInfo, usedTableTypes } = getVariantsToInfo(
        quickVariantSelectionX['variants'],
        generateParameters,
        tableParams,
        defaultTableSettings
    );
    if (!variantsToInfo) {
        return undefined;
    }
    const multiTableParams: TableParams = { ...tableParams };
    multiTableParams.multiTableInfo = {
        usedTableTypes
    };
    return {
        tableParams: multiTableParams,
        variantsToInfo
    };
}

export class MultiViewsDefinition implements v2.MultiViewsDefinition {
    // enableAutoBinding
    @syncRule(syncRuleForQuickVariantSelectionXProperty)
    enableAutoBinding?: boolean;

    // showCounts
    @syncRule(syncRuleForQuickVariantSelectionXProperty)
    showCounts?: boolean;

    // variants
    @syncRule({
        processingRuleAdapter(
            processingRule: ProcessingRule,
            schemaHandlingParams: SchemaHandlingParams,
            generateParameters: GenerateAppSchemaParameters
        ): void {
            const tableParams = schemaHandlingParams.specificParams as TableParams;
            const multiTableAllVariantsInfo = getMultiTableAllVariantsInfo(tableParams, generateParameters);
            if (multiTableAllVariantsInfo) {
                processingRule.referenceAdaptation = {
                    specificParams: multiTableAllVariantsInfo,
                    syncRuleProvider: MultiViewsVariants
                };
            } else {
                delete processingRule.element;
            }
        }
    })
    variants: { [key: string]: v2.MultiViewsVariants };
}

/**
 * This Map collects the SyncRules for those properties of class Table that are only relevant in the multi-view multi-table scenario.
 * They are exposed via function getSyncRuleForMultiTableManifestProperty (and used in class Table).
 */
const syncRulesForMultiTable = new Map<string, SyncRule>();

// Define SyncRule for property 'views'
syncRulesForMultiTable.set('views', {
    processingRuleAdapter(
        processingRule: ProcessingRule,
        schemaHandlingParams: SchemaHandlingParams,
        generateParameters: GenerateAppSchemaParameters
    ): void {
        // The views node should only be there when we are indeed in a multi-view multi-table scenario and we are still on the top level (not on the level of a view).
        const tableParams = schemaHandlingParams.specificParams as TableParams;
        if (tableParams.multiTableInfo && !tableParams.multiTableInfo.variantInfo) {
            processingRule.referenceAdaptation = {
                syncRuleProvider: MultiViewsDefinition
            };
        } else {
            delete processingRule.element;
        }
    }
});

/**
 * Define a SyncRule for one of the properties of Table that are only relevant for the single views but not for the top level table node.
 *
 * @param property - name of the property
 * @param adapt - function that adapts the schema element
 * @param onlyChartI18nKey - if provided this property will only be valid for chart variants. The value is the i18n key for the message that will be shown if the property has been set for a non-chart view.
 */
function setQuickVariantSelectionXOnlySyncRule(
    property: string,
    adapt?: ElementAdapterExtended,
    onlyChartI18nKey?: string
): void {
    // Build the analyze-function for getSyncRuleForManifestProperty:
    const analyze = function (element: Definition, schemaHandlingParams: SchemaHandlingParams): ManifestRule {
        const tableParams = schemaHandlingParams.specificParams as TableParams;
        let manifestPath: AccessorPath;
        let disallowedReason: DisallowedSpecification | undefined;
        if (tableParams.multiTableInfo?.variantInfo) {
            // If we are on the level of a variant
            manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, [
                'quickVariantSelectionX',
                'variants',
                tableParams.multiTableInfo.variantInfo.accessor
            ]);
            if (onlyChartI18nKey && !tableParams.multiTableInfo.variantInfo.isChart) {
                disallowedReason = i18next.t(onlyChartI18nKey);
            }
        } else {
            disallowedReason = DISALLOWED_WITHOUT_EXPLANATION; // As this property is only valid for quickVariantSelectionX variants we simply ignore it on top table level
        }
        return { manifestPath, disallowedReason };
    };
    syncRulesForMultiTable.set(property, getSyncRuleForManifestProperty(property, analyze, adapt));
}

// Define SyncRule for property 'entitySet'
setQuickVariantSelectionXOnlySyncRule(
    'entitySet',
    // adapt:
    function (
        element: Definition,
        schemaHandlingParams: SchemaHandlingParams,
        generateParameters: GenerateAppSchemaParameters
    ) {
        const possibleEntitySets = generateParameters.serviceAVT.entitySets.map((entitySet) => entitySet.name);
        addEnumToSchema(possibleEntitySets, element);
    } // adapt
);

// Define SyncRule for property 'annotationPath'
setQuickVariantSelectionXOnlySyncRule(
    'annotationPath',
    // adapt:
    function (element: Definition, schemaHandlingParams: SchemaHandlingParams) {
        const tableParams = schemaHandlingParams.specificParams as TableParams;
        addEnumForValidAnnotations(
            tableParams.multiTableInfo.variantInfo.entityTypeDefinition,
            [UIAnnotationTerms.SelectionPresentationVariant, UIAnnotationTerms.SelectionVariant],
            element
        );
    } // adapt
);

// Define SyncRule for property 'showItemNavigationOnChart'
setQuickVariantSelectionXOnlySyncRule('showItemNavigationOnChart', undefined, 'SHOWITEMNAVIGATIONONCHARTONLYCHART');

/**
 * Expose SyncRules for those properties of class Table that are only relevant in the multi-view multi-table scenario.
 *
 * @param property - name of the property
 * @returns the SyncRule for the given property (if the property is relevant in multi-view multi-table scenario), undefined otherwise
 */
export function getSyncRuleForMultiTableManifestProperty(property: string): SyncRule | undefined {
    return syncRulesForMultiTable.get(property);
}
